/**
 * Types for QA Content Checks dashboard panel.
 *
 * Maps to Supabase tables: qa_runs, qa_check_results
 */

// =============================================================================
// QA Run (from qa_runs table)
// =============================================================================

export type QARunStatus = 'pending' | 'running' | 'completed' | 'failed';
export type QAOverallResult = 'pass' | 'warn' | 'fail';
export type QACheckStatus = 'pass' | 'warn' | 'fail' | 'error';
export type QACheckSeverity = 'blocker' | 'warning' | 'info';

export interface QARun {
  id: string;
  content_id: string;
  tenant_id: string;
  wp_post_id: number;
  wp_post_url: string | null;
  keyword: string;
  idempotency_key: string;
  status: QARunStatus;
  overall_result: QAOverallResult | null;
  trigger_source: string;
  started_at: string;
  finished_at: string | null;
  error_message: string | null;
  created_at: string;
}

// =============================================================================
// QA Check Result (from qa_check_results table)
// =============================================================================

export interface QACheckResult {
  id: string;
  run_id: string;
  check_name: string;
  status: QACheckStatus;
  score: number | null;
  expected: string | null;
  actual: string | null;
  severity: QACheckSeverity | null;
  details_json: Record<string, unknown> | null;
  duration_ms: number | null;
  created_at: string;
}

// =============================================================================
// Filter types
// =============================================================================

export type QAContentCheckStatusFilter = 'all' | 'pass' | 'warn' | 'fail';

export interface QAContentCheckFilters {
  status: QAContentCheckStatusFilter;
  dateFrom: string;
  dateTo: string;
}

// =============================================================================
// Helpers
// =============================================================================

/** Human-readable labels for check_name values */
export const CHECK_NAME_LABELS: Record<string, string> = {
  keyword_density: 'Keyword Density',
  internal_links: 'Internal Links',
  meta_description: 'Meta Description',
  heading_keyword: 'Heading Keyword',
  external_links: 'External Links',
};

export function getCheckLabel(checkName: string): string {
  return CHECK_NAME_LABELS[checkName] || checkName.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

export function getPassedCount(checks: QACheckResult[]): number {
  return checks.filter((c) => c.status === 'pass').length;
}

export function getWpEditUrl(wpPostUrl: string | null, wpPostId: number): string | null {
  if (!wpPostUrl) return null;
  try {
    const url = new URL(wpPostUrl);
    return `${url.origin}/wp-admin/post.php?post=${wpPostId}&action=edit`;
  } catch {
    return null;
  }
}
